import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { LandingPageContext } from '../../contexts/LandingPageContext';
import i18n from '../../i18n';
import { motion } from "framer-motion";
import gsap from "gsap";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import "./NowSection.css";

const NowSection = () => {
    useTranslation();
    const navigate = useNavigate();
    const { nowSection } = useContext(LandingPageContext);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const { t } = useTranslation();
    const [showTextContent, setShowTextContent] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);
    const slideRefs = useRef([]);
    const images = useMemo(() => Array.isArray(nowSection?.images) ? nowSection.images : [], [nowSection?.images]);
    const buttonColor = nowSection.buttonTextColor || '#fff';
    const modalTitleColor = nowSection.modalTitleColor || '#ffffff';
    const modalContentColor = nowSection.modalContentColor || '#ffffff';
    const titleColor = '#A08B7A';
    const titleText = (t && typeof t === 'function') ? (t('nav.imJetzt') || t('imJetzt')) : (i18n && typeof i18n.t === 'function' ? (i18n.t('nav.imJetzt') || i18n.t('imJetzt')) : 'Im Jetzt');

    useEffect(() => {
        if (images.length === 0) return;
        images.forEach((_, idx) => {
            const el = slideRefs.current[idx];
            if (el) {
                gsap.set(el, {
                    opacity: idx === currentImageIndex ? 1 : 0,
                    scale: 1,
                    clipPath: idx === currentImageIndex ? "inset(0 0 0% 0)" : "inset(0 0 100% 0)"
                });
            }
        });
    }, [images, currentImageIndex]);

    const animateToIndex = (fromIndex, toIndex) => {
        const incoming = slideRefs.current[toIndex];
        const outgoing = slideRefs.current[fromIndex];
        if (!incoming || !outgoing) return;
        gsap.set(incoming, { opacity: 0, scale: 1.15, clipPath: "inset(0 0 100% 0)" });
        gsap.to(outgoing, { opacity: 0, scale: 1.05, duration: 1.4, ease: "power2.out" });
        gsap.to(incoming, { opacity: 1, clipPath: "inset(0 0 0% 0)", scale: 1, duration: 1.8, ease: "power3.out" });
    };

    useEffect(() => {
        if (images.length === 0) return;
        const interval = setInterval(() => {
            const prevIndex = currentImageIndex;
            const newIndex = (prevIndex + 1) % images.length;
            setCurrentImageIndex(newIndex);
            animateToIndex(prevIndex, newIndex);
        }, 5000);
        return () => clearInterval(interval);
    }, [currentImageIndex, images]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return;
        const mq = window.matchMedia('(max-width: 768px)');
        const handle = (e) => setIsMobile(e.matches);
        setIsMobile(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', handle);
        else mq.addListener(handle);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', handle);
            else mq.removeListener(handle);
        };
    }, []);

    useEffect(() => {
        if (isMobile) setShowTextContent(true);
        else setShowTextContent(false);
    }, [isMobile]);

    useEffect(() => {
        if (showTextContent) {
            gsap.fromTo(".text-content-square", { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 0.5 });
        }
    }, [showTextContent]);

    const handleOpenText = () => setShowTextContent(true);
    const handleCloseText = () => gsap.to(".text-content-square", { opacity: 0, scale: 0.9, duration: 0.5, onComplete: () => setShowTextContent(false) });

    const prevImage = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (images.length === 0) return;
        setCurrentImageIndex((prev) => { const newIndex = (prev - 1 + images.length) % images.length; animateToIndex(prev, newIndex); return newIndex; });
    };

    const nextImage = (e) => {
        if (e) { e.preventDefault(); e.stopPropagation(); }
        if (images.length === 0) return;
        setCurrentImageIndex((prev) => { const newIndex = (prev + 1) % images.length; animateToIndex(prev, newIndex); return newIndex; });
    };

    const renderParagraphWithContactLinks = (paragraph, idx) => {
        if (typeof paragraph !== 'string') return paragraph;
        const contactRegex = /(Kontaktieren Sie uns\.?|Contact us\.?)/;
        const parts = paragraph.split(contactRegex);
        for (let i = 0; i < parts.length; i++) {
            if (contactRegex.test(parts[i])) {
                if (i - 1 >= 0) parts[i - 1] = parts[i - 1].replace(/\s*\n+\s*$/, ' ');
                if (i + 1 < parts.length) parts[i + 1] = parts[i + 1].replace(/^\s*\n+\s*/, ' ');
            }
        }
        return parts.map((part, i) => {
            if (part === null || part === undefined) return null;
            if (contactRegex.test(part)) {
                return (
                    <a key={`contact-${idx}-${i}`} href="/contact"
                        onClick={(e) => { e.preventDefault(); navigate('/contact'); }}
                        style={{ color: '#fff', textDecoration: 'underline' }}>
                        {part.trim()}
                    </a>
                );
            }
            const lines = part.split('\n');
            return lines.map((line, lineIdx) => (
                <React.Fragment key={`text-${idx}-${i}-${lineIdx}`}>
                    {line}{lineIdx < lines.length - 1 ? <br /> : null}
                </React.Fragment>
            ));
        });
    };

    return (
        <>
            <div className="section-title" id="nowSection" style={{ color: titleColor }}>
                {titleText}
            </div>
            <div className="now-section">
                <div className="now-carousel">
                    {images.length > 0 && images.map((img, i) => (
                        <div key={i} ref={(el) => (slideRefs.current[i] = el)} className="background-carousel enhanced-slide" style={{ backgroundImage: `url(${img})` }} />
                    ))}
                    {images.length > 1 && (
                        <>
                            <button type="button" aria-label="Previous image" className="nav-btn prev" onClick={prevImage} onTouchEnd={prevImage}>&#8249;</button>
                            <button type="button" aria-label="Next image" className="nav-btn next" onClick={nextImage} onTouchEnd={nextImage}>&#8250;</button>
                        </>
                    )}
                </div>

                {!isMobile && !showTextContent && (
                    <motion.div className="tag-square" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} onClick={handleOpenText}>
                        <div className="tag-buttons">
                            <button className="now-button" style={{ color: buttonColor }} onClick={handleOpenText}>
                                {typeof nowSection.buttonText === 'object' ? nowSection.buttonText[i18n.language] || nowSection.buttonText.de || nowSection.buttonText.en : nowSection.buttonText}
                            </button>
                        </div>
                    </motion.div>
                )}

                {(showTextContent || isMobile) && (
                    <motion.div className="text-content-square" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} style={{ color: modalContentColor }}>
                        <button className="close-btn" style={{ color: modalTitleColor }} onClick={handleCloseText}>&times;</button>
                        <div className="text-content">
                            <div className="text-content-title" style={{ color: modalTitleColor }}>
                                {typeof nowSection.modalTitle === 'object' ? nowSection.modalTitle[i18n.language] || nowSection.modalTitle.de || nowSection.modalTitle.en : nowSection.modalTitle}
                            </div>
                            {(Array.isArray(nowSection.modalContent) ? nowSection.modalContent : (nowSection.modalContent && typeof nowSection.modalContent === 'object' ? nowSection.modalContent[i18n.language] || nowSection.modalContent.de || [] : nowSection.modalContent || [])).map((paragraph, index) => (
                                <p key={index} style={{ color: modalContentColor }}>{renderParagraphWithContactLinks(paragraph, index)}</p>
                            ))}
                        </div>
                    </motion.div>
                )}

                {isModalOpen && (
                    <div className="modal-backdrop" onClick={() => setIsModalOpen(false)}>
                        <div className="small-modal-content" onClick={(e) => e.stopPropagation()}>
                            <button className="modal-close" style={{ color: modalTitleColor }} onClick={() => setIsModalOpen(false)}>&times;</button>
                            <h2 style={{ color: modalTitleColor }}>{typeof nowSection.artInsights === 'object' ? nowSection.artInsights[i18n.language] || nowSection.artInsights.de || nowSection.artInsights.en : nowSection.artInsights}</h2>
                            <p style={{ color: modalContentColor }}>{typeof nowSection.artInsightsContent === 'object' ? nowSection.artInsightsContent[i18n.language] || nowSection.artInsightsContent.de || nowSection.artInsightsContent.en : nowSection.artInsightsContent}</p>
                            <a href="/gallery" style={{ color: '#bfa17a', textDecoration: 'underline' }}
                                onClick={(e) => { e.preventDefault(); setIsModalOpen(false); navigate('/gallery'); }}>Go to Gallery</a>
                        </div>
                    </div>
                )}

                <div className="events-cta-row">
                    <button className="events-float-btn" aria-label="Upcoming Events" onClick={() => navigate('/events')}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                            <path d="M3 7h18M7 3v4M17 3v4M21 21H3V10h18v11z" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span className="events-float-text">Upcoming Events</span>
                    </button>
                </div>
            </div>
        </>
    );
};

export default NowSection;
